export const OFFENSE_MAP_COLLECTION = "POLICE_OFFENSE_MAPS";
export const OFFENSE_MAP_INDEX = "index";

export function offenseCatalog(index) {
  return Object.entries(index?.offenses || {})
    .map(([id, value]) => ({ id, ...value }))
    .sort((left, right) => left.name.localeCompare(right.name));
}

export function offenseDocumentIds(entry) {
  if (!entry || !Number.isInteger(entry.parts) || entry.parts < 1) return [];
  if (entry.parts === 1) return [entry.id];
  return Array.from({ length: entry.parts }, (_, index) => `${entry.id}-p${String(index + 1).padStart(3, "0")}`);
}

function dictionaryValue(values, index) {
  if (index === -1) return null;
  if (!Number.isInteger(index) || index < 0 || index >= values.length) throw new Error("Invalid offense map dictionary index");
  return values[index];
}

export function decodeOffenseDocuments(documents) {
  return documents
    .sort((left, right) => left.part - right.part)
    .flatMap((document) => {
      const columns = [document.lat, document.lng, document.receivedWallTimeMs, document.cfsNumbers,
        document.streetIds, document.callTypeIds, document.dispositionIds];
      if (!columns.every((column) => Array.isArray(column) && column.length === document.pointCount)) {
        throw new Error("Offense map columns are incomplete");
      }
      return document.lat.map((latitude, index) => ({
        latitude: latitude / document.coordinateScale,
        longitude: document.lng[index] / document.coordinateScale,
        receivedWallTimeMs: document.receivedWallTimeMs[index],
        cfsNumber: document.cfsNumbers[index] || null,
        streetName: dictionaryValue(document.streets || [], document.streetIds[index]),
        callType: dictionaryValue(document.callTypes || [], document.callTypeIds[index]),
        disposition: dictionaryValue(document.dispositions || [], document.dispositionIds[index]),
      }));
    });
}

export function availableOffenseYears(points) {
  return Array.from(new Set(points.map((point) => new Date(point.receivedWallTimeMs).getUTCFullYear()))).sort((a, b) => a - b);
}

export function availableReportYears(points, offense) {
  return Array.from(new Set([
    ...availableOffenseYears(points),
    ...Object.keys(offense?.totalOffensesByYear || {}).map(Number),
    ...Object.keys(offense?.clearedByArrestByYear || {}).map(Number),
  ])).filter(Number.isInteger).sort((a, b) => a - b);
}

export function totalMetricForYears(values, fromYear, throughYear) {
  if (fromYear == null || throughYear == null) return 0;
  return Object.entries(values || {}).reduce((total, [year, value]) => {
    const numericYear = Number(year);
    return numericYear >= fromYear && numericYear <= throughYear ? total + Number(value || 0) : total;
  }, 0);
}

export function groupedMetricForYears(values, fromYear, throughYear) {
  if (fromYear == null || throughYear == null) return [];
  const totals = new Map();
  for (const [year, groups] of Object.entries(values || {})) {
    const numericYear = Number(year);
    if (numericYear < fromYear || numericYear > throughYear || !groups) continue;
    for (const [label, value] of Object.entries(groups)) {
      totals.set(label, (totals.get(label) || 0) + Number(value || 0));
    }
  }
  return [...totals].map(([label, value]) => ({ label, value }));
}

export function filterOffensePoints(points, fromYear, throughYear) {
  return points.filter((point) => {
    const year = new Date(point.receivedWallTimeMs).getUTCFullYear();
    return year >= fromYear && year <= throughYear;
  });
}

export function groupOffenseLocations(points) {
  const grouped = new Map();
  for (const point of points) {
    const key = `${point.latitude},${point.longitude}`;
    const existing = grouped.get(key);
    if (!existing) grouped.set(key, { ...point, count: 1, latest: point });
    else {
      existing.count++;
      if (point.receivedWallTimeMs > existing.latest.receivedWallTimeMs) existing.latest = point;
    }
  }
  return [...grouped.values()];
}

export function formatWallTime(milliseconds) {
  return new Intl.DateTimeFormat("en-US", {
    year: "numeric", month: "short", day: "numeric", hour: "numeric", minute: "2-digit", timeZone: "UTC",
  }).format(new Date(milliseconds));
}
